import React, { useEffect, useState } from "react";
import { Container, Row, Col, Button, Spinner } from "reactstrap";
import { useNavigate } from "react-router-dom";
import styles from "../../../../modules/Library/AdverseEvent/css/adverseEvent.module.scss";
import ip from "../../IPinventory/css/ipInventory.module.scss";
import { RETURNS_LOG_PATHS } from "Routes/paths";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import { ReasonSubmitPayload } from "../../../../common/utilities/Reason/Reason";
import CommonTableIndexWise from "../../../../Components/Common/CommonTableIndexWise";
import CommonSearch from "../../../../Components/Common/CommonSearch";
import { useReturnsAccountability } from "../api/useReturnsAccountability";
import {
  formatDate,
  GetUserData,
  perPageData,
  showToastMessage,
} from "../../../../common/service/CommonService";
import { queryClient } from "../../../../api/queryClient";
import {
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
} from "../../../../common/constants/PermissionConstants";
import CommonButton from "../../../../Components/Common/CommonButton";
import PinVerificationModal from "../../DailyReconsilation/components/PinVerificationModal";
import StatCard from "../../../../Components/Common/StatCard";
import FilePreviewModal from "../../../Study/Attachments/components/FilePreviewModal";
import { useGlobalStudy } from "context/StudyContext";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import NoStudySelectedPlaceholder from "../../../../Components/Common/NoStudySelectedPlaceholder";

/* ------------------------------------------------------------------ */
/* Interfaces                                                            */
/* ------------------------------------------------------------------ */

interface ReturnRecord {
  returnid: number;
  returnno: string;
  studyprotocol: string;
  studycode: string;
  subjectsource: string;
  productname: string;
  batchno: string;
  returnedqty: number;
  unit: string;
  returndate: string;
  conditionreturned: string;
  reasonforreturn: string;
  total_count: number;
  isverified?: boolean;
  studyid?: number;
  pending_verification?: number;
  completed_verification?: number;
}

const LISTReturnsLog: React.FC = () => {
  document.title = "Returns & Accountability | ZENTIXS ORBIT";
  const navigate = useNavigate();
  const userData = GetUserData();

  const [searchText, setSearchText] = useState<string>("");
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(1);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [recordToDelete, setRecordToDelete] = useState<ReturnRecord | null>(
    null,
  );
  const [showDeleteReason, setShowDeleteReason] = useState<boolean>(false);

  const [verifyModal, setVerifyModal] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<ReturnRecord | null>(
    null,
  );

  //Audit
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const { SelectAllMutation: AuditMutation } = useAuditService();
  const { selectedStudy: globalStudy } = useGlobalStudy();


  const onAuditClick = () => {
    const payload = {
      modulename: "Returns Log",
      studyid: globalStudy?.studyid
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "Returns_Log_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  const { SelectAllQuery, DeleteMutation, verifyPinMutation } = useReturnsAccountability({
    searchText,
    pageNumber: currentPageNumber,
    pageSize: 5,
    studyid: globalStudy?.studyid,
    returnSelectAll: true,
  });

  const {
    data: queryResponse,
    isLoading,
    isFetching,
    isError,
  } = SelectAllQuery as {
    data?: { data?: any[] };
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
  };

  const data: ReturnRecord[] = (queryResponse?.data ?? []).map((row: any) => ({
    returnid: row.returnid ?? row.id ?? row.return_id ?? 0,
    returnno: row.returnno,
    studyprotocol: row.studyprotocol ?? row.studycode ?? "",
    studycode: row.studycode ?? "",
    subjectsource: row.subjectno ?? row.subjectsource ?? "",
    productname: row.investiproduct ?? row.productname ?? "",
    batchno: row.batchno ?? row.batchlotno ?? "",
    returnedqty: row.quantityreturned ?? row.returnedqty ?? 0,
    unit: row.unittype ?? row.unit ?? "",
    returndate: row.returndate ? row.returndate : "",
    conditionreturned: String(row.conditionreturned ?? ""),
    reasonforreturn: row.reasonforreturn ?? row.returnreason ?? "",
    total_count: row.total_count ?? 0,
    returnsourcetypetext: row.returnsourcetypetext ?? "",
    studyid: row.studyid ?? 0,
    isverified: row.isverified ?? false,
    ipcategory: row.ipcategory || "",
    unittypetext: row.unittypetext || "",
    conditiontext: row.conditionreturnedtext || "",
    reasontext: row.reasonforreturntext || "",
    verifiedbyname: row.verifiedbyname || "",
    verifieddate: row.verifieddate || "",
    pending_verification: row.pending_verification ?? 0,
    completed_verification: row.completed_verification ?? 0,
  }));

  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [pendingVerification, setPendingVerification] = useState<number>(0);
  const [completedVerification, setCompletedVerification] = useState<number>(0);

  useEffect(() => {
    if (data.length > 0) {
      setTotalRecords(data[0].total_count || 0);
      setPendingVerification(data[0].pending_verification || 0);
      setCompletedVerification(data[0].completed_verification || 0);
    }
  }, [data]);

  const onSearch = (value: string | null) => {
    setSearchText(value ?? "");
    setCurrentPageNumber(1);
  };

  const handleDeleteClick = (record: ReturnRecord) => {
    setRecordToDelete(record);
    setDeleteModal(true);
  };

  const handleDeleteConfirm = (payload: ReasonSubmitPayload) => {
    const deletePayload = {
      returnid: payload.id,
      updatereason: payload.reason,
    };

    DeleteMutation.mutate(deletePayload, {
      onSuccess: (res: any) => {
        showToastMessage(
          "success",
          res?.message || "Record deleted successfully",
        );
        queryClient.invalidateQueries({
          queryKey: ["returns-accountability-select-all"],
        });
        queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
        setCurrentPageNumber(1);
        setDeleteModal(false);
        setShowDeleteReason(false);
        setRecordToDelete(null);
      },
      onError: (error: any) => {
        showToastMessage("error", error?.message || "Delete failed");
      },
    });
  };

  const columns: any = [
    {
      key: "return_info",
      label: "RETURN & STUDY",
      type: "link",
      onClick: (row: any) =>
        navigate(
          RETURNS_LOG_PATHS.VIEW.replace(":id", row.returnid.toString()),
        ),
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.link}>{row.returnno}</p>
            <p className={ip.cellSecondary}>{row.studycode}</p>
          </>
        );
      },
    },
    {
      key: "subject_source",
      label: "SUBJECT / SOURCE",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>{row.subjectsource}</p>
            <p className={ip.cellSecondary}>{row.returnsourcetypetext}</p>
          </>
        );
      },
    },
    {
      key: "product_details",
      label: "PRODUCT & BATCH",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>{row.productname}</p>
            <p className={ip.cellSecondary}>
              {row.ipcategory} | {row.batchno}
            </p>
          </>
        );
      },
    },
    {
      key: "returned_qty",
      label: "QTY",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <div className="d-flex align-items-center gap-2">
              <span className={ip.quantityBadge}>{row.returnedqty}</span>
              <p className={ip.cellSecondary} style={{ marginBottom: 0 }}>
                {row.unittypetext}
              </p>
            </div>
          </>
        );
      },
    },
    {
      key: "condition_reason",
      label: "CONDITION & REASON",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>{row.conditiontext}</p>
            <p className={ip.cellSecondary}>{row.reasontext}</p>
          </>
        );
      },
    },
    {
      key: "verification_date",
      label: "VERIFIED BY",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <div className="d-flex flex-column">
              <p className={ip.cellPrimary} style={{ marginBottom: 0 }}>
                {row.verifiedbyname || "—"}
              </p>
              <p className={ip.cellSecondary}>{formatDate(row.verifieddate)}</p>
            </div>
          </>
        );
      },
    },
  ];

  const getRowActions = (row: ReturnRecord): any => {
    const isVerified = row?.isverified;

    const actions: any = {};
    if (!isVerified) {
      actions.onVerify = (r: any) => handleVerifyClick(r);
      actions.onEdit = (row: any) =>
        navigate(
          RETURNS_LOG_PATHS.EDIT.replace(":id", row.returnid.toString()),
        );
      actions.onDelete = (row: any) => handleDeleteClick(row);
    }
    return actions;
  };
  const handleVerifyClick = (record: ReturnRecord) => {
    setSelectedRecord(record);
    setVerifyModal(true);
  };

  const onVerifyPinSubmit = (password: string) => {
    if (!selectedRecord) return;

    const payload = {
      verifypin: password,
      returnid: Number(selectedRecord.returnid),
      studyid: Number(selectedRecord.studyid),
      verifiedby: Number(userData?.userid),
    };

    verifyPinMutation.mutate(payload, {
      onSuccess: (response: any) => {
        queryClient.invalidateQueries({
          queryKey: ["returns-accountability-select-all"],
        });
        queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
        showToastMessage("success", response?.message);
        setVerifyModal(false);
        setSelectedRecord(null);
      },
      onError: (error: any) => {
        showToastMessage(
          "error",
          error?.response?.data?.message || "Invalid password",
        );
      },
    });
  };

  return (
    <div className={styles.dashboardContainer}>
      <Container fluid>
        {!globalStudy?.studyid ? (
          <div >
            <NoStudySelectedPlaceholder moduleName="returns & accountability" />
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mt-4 mb-3 px-2">
              <div>
                <h5 className={`mb-1 ${styles.pageHeaderTitle}`}>
                  Returns & Accountability
                </h5>
                <p className={styles.pageHeaderSubtitle}>
                  Manage and document the return, reconciliation, and destruction of
                  investigational products.
                </p>
              </div>
              <div className="d-flex gap-3 align-items-center">
                <CommonButton
                  module={PERMISSION_MODULES.RETURNS_LOG}
                  action={PERMISSION_ACTIONS.AUDIT}
                  color="primary"
                  onClick={onAuditClick}
                  disabled={AuditMutation.isPending}
                  outline
                  size="sm"
                  className="fw-600 "
                >
                  {AuditMutation.isPending ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
                <CommonButton
                  color="primary"
                  outline
                  className="px-2 fw-600"
                  size="sm"
                  module={PERMISSION_MODULES.RETURNS_LOG}
                  action={PERMISSION_ACTIONS.ADD}
                  onClick={() => navigate(RETURNS_LOG_PATHS.ADD)}
                >
                  <i className="ri-add-line me-1" /> Log New Return
                </CommonButton>
              </div>
            </div>

            {/* ── Stats & Table Card ── */}
            {/* ── Stat Cards ── */}
            <Row className="g-3 mb-3 px-1">
              <Col md={4}>
                <StatCard
                  title="TOTAL RETURNS"
                  value={totalRecords}
                  subtext="Across active studies"
                  icon={<i className="ri-arrow-go-back-line" />}
                  bgColor="#eff6ff"
                  iconColor="#2563eb"
                  trendType="info"
                  subtextIcon="ri-file-list-3-line"
                />
              </Col>

              <Col md={4}>
                <StatCard
                  title="PENDING VERIFICATION"
                  value={pendingVerification}
                  subtext="Needs review and approval"
                  icon={<i className="ri-time-line" />}
                  bgColor="#fffbeb"
                  iconColor="#d97706"
                  trendType="negative"
                  subtextIcon="ri-error-warning-line"
                />
              </Col>

              <Col md={4}>
                <StatCard
                  title="COMPLETED VERIFICATION"
                  value={completedVerification}
                  subtext="Verified return records"
                  icon={<i className="ri-verified-badge-line" />}
                  bgColor="#ecfdf5"
                  iconColor="#059669"
                  trendType="success"
                  subtextIcon="ri-check-double-line"
                />
              </Col>
            </Row>

            <div className={styles.listPanel}>
              <div className={styles.panelHeader}>
                <h2>
                  <i className="ri-arrow-go-back-line me-2 text-primary fs-18" />
                  IP Return Records
                </h2>

                <CommonSearch onSearch={onSearch} searchText={searchText} />
              </div>

              <div className="px-4">
                <CommonTableIndexWise
                  moduleName={PERMISSION_MODULES.RETURNS_LOG}
                  columns={columns}
                  data={data}
                  loading={isLoading || isFetching}
                  pageSize={5}
                  currentPage={currentPageNumber}
                  totalRecords={data[0]?.total_count || 0}
                  onPageChange={setCurrentPageNumber}
                  actions={getRowActions}
                />
              </div>
            </div>
          </>
        )}

        {/* File Preview Modal */}
        {isPreviewOpen && (
          <FilePreviewModal
            file={previewFile}
            isOpen={isPreviewOpen}
            size={"xl"}
            onClose={() => {
              setIsPreviewOpen(false);
              setPreviewFile(null);
            }}
          />
        )}

        {deleteModal && (
          <DeleteModal
            isopen={deleteModal}
            recordId={recordToDelete?.returnid}
            recordName={recordToDelete?.returnno}
            toggle={() => setDeleteModal(!deleteModal)}
            onDeleteClick={handleDeleteConfirm}
            isLoading={DeleteMutation.isPending}
            showDeleteReason={showDeleteReason}
            toggleDeleteReason={() => setShowDeleteReason((p) => !p)}
          />
        )}

        <PinVerificationModal
          isOpen={verifyModal}
          toggle={() => setVerifyModal(false)}
          onVerify={onVerifyPinSubmit}
          isLoading={verifyPinMutation.isPending}
          title="Verify Return Log"
          description={
            <>
              Please enter your password to verify Return Log Information: <strong>{selectedRecord?.returnno}</strong>
            </>
          }
          isInvalid={verifyPinMutation.isError}
        />
      </Container>
    </div>
  );
};

export default LISTReturnsLog;
